import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import axios, { isAxiosError, type CancelTokenSource } from 'axios'
import { DayjsFormats, logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import { getDropdownItemsFromStatusConfig } from 'shared/ui/Status'
import { uiStore } from 'shared/store/uiStore'
import { BillingApi } from 'entities/Billing/api/billing'
import { tableLimitStore } from 'entities/TableLimit'
import { type IPaymentStatusApi, type IResponseBillingPayment } from 'entities/Billing/api/types'
import { paymentStatusConfig } from 'pages/settings/pages/billing/pages/overview/ui/PaymentHistory/config'
import { PaymentHistoryRow } from '../model/PaymentHistoryRow'

const statusDropdownItems = getDropdownItemsFromStatusConfig(paymentStatusConfig)

export class PaymentHistoryStore {
  private _payments: IResponseBillingPayment[] = []
  private _loading = false
  private _tableStore: TableStore<PaymentHistoryRow>
  private _calendarFilterStore: CalendarFilterStore
  private _selectedStatuses: IPaymentStatusApi[] = []
  private _cancelTokenSource: CancelTokenSource | null = null
  private _hasRetriedAtMinLimit = false
  private _disposeCalendarFilterReaction: IReactionDisposer | null = null

  constructor(createdAt?: string | null) {
    this._tableStore = new TableStore<PaymentHistoryRow>({
      key: 'paymentHistoryTable',
      limit: 10,
      getLimit: () => tableLimitStore.getLimit('payment-history-table'),
      setLimit: (value) => tableLimitStore.setLimit('payment-history-table', value),
    })
    this._calendarFilterStore = new CalendarFilterStore(createdAt)
    this._calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.last30days)
    this._calendarFilterStore.setParamsDate()

    makeAutoObservable(this)

    this._disposeCalendarFilterReaction = reaction(
      () => this._calendarFilterStore.sendDate,
      () => {
        this._tableStore.setPage(1)
        this.fetchPayments()
      }
    )

    this.init()
  }

  get payments() {
    return this._payments
  }

  get loading() {
    return this._loading
  }

  get tableStore() {
    return this._tableStore
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get selectedStatuses() {
    return this._selectedStatuses
  }

  get statusDropdownItems() {
    return statusDropdownItems
  }

  get paymentRows(): PaymentHistoryRow[] {
    return this.payments.map(
      (payment) =>
        new PaymentHistoryRow({
          id: payment.id,
          payment,
        })
    )
  }

  get isEmpty() {
    return !this._loading && this._payments.length === 0
  }

  get hasActiveFilters() {
    return (
      this._selectedStatuses.length > 0 ||
      this._calendarFilterStore.selectedCalendarFilter !== CalendarFilterEnum.allTime
    )
  }

  init = async () => {
    await this.fetchPayments()
  }

  setSelectedStatuses = (statuses: IPaymentStatusApi[]) => {
    this._selectedStatuses = statuses
    this._tableStore.setPage(1)
    this.fetchPayments()
  }

  private initCancelTokenSource = () => {
    if (this._cancelTokenSource) {
      this._cancelTokenSource.cancel()
    }
    this._cancelTokenSource = axios.CancelToken.source()
  }

  fetchPayments = async () => {
    let retrying = false

    try {
      runInAction(() => {
        this._loading = true
      })

      this.initCancelTokenSource()

      const sendDate = this._calendarFilterStore.sendDate
      const from = sendDate?.from
        ? uiStore.dayjs(sendDate.from).format(DayjsFormats.timestampServerDate)
        : undefined
      const to = sendDate?.to
        ? uiStore.dayjs(sendDate.to).format(DayjsFormats.timestampServerDate)
        : undefined

      const { data } = await BillingApi.getBillingPayments(
        {
          page: this._tableStore.page,
          per_page: this._tableStore.limit,
          from,
          to,
          statuses: this._selectedStatuses.length > 0 ? this._selectedStatuses : undefined,
        },
        {
          cancelToken: this._cancelTokenSource?.token,
        }
      )

      runInAction(() => {
        this._hasRetriedAtMinLimit = false
        this._payments = data.data
        this._tableStore.setTotal(data.total)
      })
    } catch (e) {
      const isTimeout =
        isAxiosError(e) && (e.response?.status === 408 || e.response?.status === 504)
      if (isTimeout) {
        logger.warn(
          'TABLE: payment-history-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
        )
        const currentLimit = this._tableStore.limit
        const newLimit = tableLimitStore.reduceLimit('payment-history-table', currentLimit)
        this._tableStore.setLimit(newLimit)
        if (newLimit !== currentLimit) {
          this._hasRetriedAtMinLimit = false
          retrying = true
          this.fetchPayments()
        } else if (!this._hasRetriedAtMinLimit) {
          this._hasRetriedAtMinLimit = true
          retrying = true
          this.fetchPayments()
        }
      } else if (!axios.isCancel(e)) {
        logger.error(e)
      }
    } finally {
      if (!retrying) {
        runInAction(() => {
          this._loading = false
        })
      }
    }
  }

  onChangePagination = (page: number, limit: number) => {
    this._tableStore.setPage(page)
    this._tableStore.setLimit(limit)
    this.fetchPayments()
  }

  onRowClick = async (row: PaymentHistoryRow) => {
    try {
      const { data } = await BillingApi.getBillingPaymentLink(row.origin.payment.invoice_id)
      if (data.url) {
        window.open(data.url, '_blank')
      }
    } catch (e) {
      logger.error(e)
    }
  }

  dispose = () => {
    this._disposeCalendarFilterReaction?.()
    this._cancelTokenSource?.cancel()
  }
}
